import React from 'react';

type Props = {
  label?: string;
  // Visuals
  textColor?: string; // hex or css color
  bgMode?: 'solid' | 'gradient' | 'invert';
  bgColor?: string; // hex for solid
  gradFrom?: string; // hex for gradient
  gradTo?: string;   // hex for gradient
  radius?: number;   // px
  shadow?: boolean;
  fontFamily?: string;
  height?: number;   // px
  paddingX?: number; // px
  fontSize?: number; // px
  glow?: boolean;
  outlineColor?: string; // css color
  outlineWidth?: number; // px
  capsule?: boolean; // if true, use height/2 radius
  outlineMode?: 'color' | 'gradient' | 'invert';
  outlineGradFrom?: string;
  outlineGradTo?: string;
};

const ButtonOverlay: React.FC<Props> = ({
  label = 'Click me',
  textColor = '#ffffff',
  bgMode = 'solid',
  bgColor = '#2563eb',
  gradFrom = '#2563eb',
  gradTo = '#4f46e5',
  radius = 10,
  shadow = true,
  fontFamily,
  height = 36,
  paddingX = 16,
  fontSize = 14,
  glow = false,
  outlineColor,
  outlineWidth = 0,
  capsule = false,
  outlineMode = 'color',
  outlineGradFrom = '#2563eb',
  outlineGradTo = '#4f46e5',
}) => {
  // Compose shadows: optional outline ring (spread), optional glow ring, and drop shadows
  const ring = outlineWidth > 0 && outlineMode==='color' ? `0 0 0 ${outlineWidth}px ${outlineColor || (bgMode==='gradient' ? gradTo : bgColor)}` : '';
  const glowRing = glow ? `0 0 0 4px ${(gradTo || bgColor)}33` : '';
  const drops = '0 6px 16px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.12)';
  const parts = [ring, glow ? glowRing : '', shadow ? drops : ''].filter(Boolean);
  const style: React.CSSProperties = {
    color: textColor,
    borderRadius: capsule ? Math.max(0, height / 2) : radius,
    background:
      bgMode === 'gradient'
        ? `linear-gradient(90deg, ${gradFrom}, ${gradTo})`
        : (bgMode === 'invert' ? 'rgba(255,255,255,0.01)' : bgColor),
    backdropFilter: bgMode === 'invert' ? 'invert(1)' : undefined,
    WebkitBackdropFilter: bgMode === 'invert' ? 'invert(1)' : undefined,
    boxShadow: parts.length ? parts.join(', ') : 'none',
    fontFamily: fontFamily || undefined,
    height,
    padding: `0 ${paddingX}px`,
    fontSize,
  };
  if (outlineWidth > 0 && outlineMode==='gradient') {
    // Render gradient outline as an outer wrapper with padding, so it grows outward
    const wrapperRadius = (capsule ? Math.max(0, height/2) : radius) + outlineWidth;
    const wrapperStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(90deg, ${outlineGradFrom}, ${outlineGradTo})`,
      padding: outlineWidth,
      borderRadius: wrapperRadius,
      boxShadow: parts.filter(p=>p!==ring).join(', ') || undefined,
    };
    return (
      <div>
        <span className="inline-flex" style={wrapperStyle}>
          <span className="inline-flex items-center justify-center font-medium" style={style}>{label}</span>
        </span>
      </div>
    );
  }
  if (outlineWidth > 0 && outlineMode==='invert') {
    const wrapperRadius = (capsule ? Math.max(0, height/2) : radius) + outlineWidth;
    const wrapperStyle: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Transparent background to trigger backdrop-filter region
      background: 'rgba(255,255,255,0.01)',
      backdropFilter: 'invert(1)',
      WebkitBackdropFilter: 'invert(1)',
      padding: outlineWidth,
      borderRadius: wrapperRadius,
      boxShadow: parts.filter(p=>p!==ring).join(', ') || undefined,
    };
    return (
      <div>
        <span className="inline-flex" style={wrapperStyle}>
          <span className="inline-flex items-center justify-center font-medium" style={style}>{label}</span>
        </span>
      </div>
    );
  }
  return (
    <div>
      <span className="inline-flex items-center justify-center font-medium" style={style}>{label}</span>
    </div>
  );
};

export default ButtonOverlay;
